import { endOfMonth, endOfQuarter, formatDate, startOfMonth, subMonths, subQuarters } from "date-fns";

import { MAX_ANALYTICS_PERCENTAGE_VALUE } from "@/helpers/analytics";

import type { Period } from "./types";

/**
 * Rounds a value up to one decimal place.
 * Mirrors backend's MathHelpers.Ceiling() behavior.
 * Example: 7.31 -> 7.4, 7.30 -> 7.3
 */
export const ceilingToOneDecimal = (value: number): number => {
  return Math.ceil(value * 10) / 10;
};

export const formatAnalyticsRawValue = (value: number): string => {
  return ceilingToOneDecimal(value).toFixed(1);
};

export const formatAnalyticsPercentageValue = (value: number): string => {
  // Cap at 100%
  const percentage = Math.min(value, MAX_ANALYTICS_PERCENTAGE_VALUE);

  // Round up to the nearest integer
  return `${Math.ceil(percentage)}%`;
};

export const formatAnalyticsDatePayload = (date: Date): string => {
  return formatDate(date, "yyyy-MM-dd");
};

export const derivePrimaryDateRangeFromPeriod = (
  period: Period,
): { startDate: Date | undefined; endDate: Date | undefined } => {
  if (period.period === "last30Days") {
    return { startDate: undefined, endDate: undefined };
  } else {
    const startDate = period.startDate;
    const endDate = endOfQuarter(startDate);

    return { startDate, endDate };
  }
};

export const deriveSecondaryDateRangeFromPeriod = (period: Period): { startDate: Date; endDate: Date } => {
  if (period.period === "last30Days") {
    // Compare to the last full month
    // e.g. Last 30 days is March 15 - April 14, so compare to Feb 1 - Feb 28
    const dateStartOfMonth = startOfMonth(subMonths(new Date(), 2));

    return {
      startDate: dateStartOfMonth,
      endDate: endOfMonth(dateStartOfMonth),
    };
  } else {
    const secondaryStart = subQuarters(period.startDate, 1);
    const endDate = endOfQuarter(secondaryStart);

    return {
      startDate: secondaryStart,
      endDate,
    };
  }
};
